import React from 'react'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'

import { H2, Text } from '@mxenabled/mxui'

import StatusBar from '../../common/components/charts/StatusBar'

import StatusChip from './StatusChip'

export type ProgressBarStatus = 'GOOD' | 'FAIR' | 'OFF_TRACK' | 'MISSING'

interface StatusBarDataItem {
  percentage: string
  color?: string
}

export interface ProgressBarProps {
  label: string
  value: string
  status: ProgressBarStatus
  bottomComponent: React.ReactNode
  statusBarData: StatusBarDataItem[]
}

const ProgressBar: React.FC<ProgressBarProps> = observer(
  ({ label, value, status, bottomComponent, statusBarData = [] }) => {
    const cardStateStyles = {
      GOOD: {
        progressBarColor: 'primary.main',
      },
      FAIR: {
        progressBarColor: 'warning.main',
      },
      OFF_TRACK: {
        progressBarColor: 'error.main',
      },
      MISSING: {
        progressBarColor: 'text.secondary',
      },
    }[status]

    const statusBarColorData = statusBarData.map((item, index) => {
      if (statusBarData.length === 2) {
        return {
          color: index === 0 ? 'primary.main' : item.color || cardStateStyles?.progressBarColor,
          percentage: item.percentage,
        }
      }

      return {
        color: item.color || cardStateStyles?.progressBarColor,
        percentage: item.percentage,
      }
    })

    return (
      <Stack
        sx={{
          backgroundColor: 'background.paper',
          gap: 8,
          p: 24,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack sx={{ gap: 2 }}>
          <Text variant="body2">{label}</Text>
          <Stack
            sx={{
              alignItems: 'center',
              flexDirection: 'row',
              gap: 12,
              justifyContent: 'space-between',
            }}
          >
            <H2>{value}</H2>
            <StatusChip status={status} />
          </Stack>
        </Stack>

        <Stack>
          <StatusBar data={statusBarColorData} height={16} />
          {bottomComponent}
        </Stack>
      </Stack>
    )
  },
)

export default ProgressBar
